import api from "../api";
import type { PartSearchResult } from "../models/PartSearch";

type ApiResponse<T> = { statusCode: number; message?: string; data?: T };
type SearchDicRawItem = {
  masterAssemblyId?: number | string | null;
  masterAssemblyVersionId?: number | string | null;
  currentVersionId?: number | string | null;
  masterPartNumber?: string | null;
  masterAssemblyDescription?: string | null;
  figId?: number | string | null;
  figNumber?: string | null;
  figTitle?: string | null;
  itemNumber?: string | number | null;
  partNumber?: string | null;
  partDescription?: string | null;
};

export type PartsSearchDictionaryResult = {
  masterAssemblyId: number;
  currentVersionId: number | null;
  masterPartNumber: string;
  masterAssemblyDescription: string;
  figId: number | null;
  figNumber: string;
  figTitle: string;
  itemNumber: string;
  partNumber: string;
  partDescription: string;
};

const normalizeItem = (item: any): PartSearchResult | null => {
  const partNumber =
    item?.componentPartNumber ??
    item?.partNumber ??
    item?.componentNumber ??
    item?.partNo ??
    "";
  if (!partNumber) return null;
  const description =
    item?.componentDescription ??
    item?.description ??
    item?.partDescription ??
    "";
  const masterAssemblyId =
    item?.masterAssemblyId != null ? Number(item.masterAssemblyId) : undefined;
  return {
    masterAssemblyId: Number.isNaN(masterAssemblyId) ? undefined : masterAssemblyId,
    componentPartNumber: String(partNumber),
    componentDescription: description ? String(description) : "",
  };
};

export async function searchParts(
  oem: string,
  keyword: string
): Promise<PartSearchResult[]> {
  if (!keyword.trim()) return [];
  const { data } = await api.get<ApiResponse<any[]>>(
    `/Parts/search-part/${oem}/${encodeURIComponent(keyword.trim())}`
  );
  if (data?.statusCode === 200 && Array.isArray(data.data)) {
    return data.data.map(normalizeItem).filter(Boolean) as PartSearchResult[];
  }
  return [];
}

const normalizeSearchDicItem = (item: SearchDicRawItem): PartsSearchDictionaryResult | null => {
  const masterAssemblyId = Number(item?.masterAssemblyId);
  if (!Number.isFinite(masterAssemblyId) || masterAssemblyId <= 0) return null;

  const currentVersionIdRaw = Number(item?.currentVersionId ?? item?.masterAssemblyVersionId);
  const currentVersionId = Number.isFinite(currentVersionIdRaw) ? currentVersionIdRaw : null;

  const figIdRaw = Number(item?.figId);
  const figId = Number.isFinite(figIdRaw) && figIdRaw > 0 ? figIdRaw : null;

  return {
    masterAssemblyId,
    currentVersionId,
    masterPartNumber: String(item?.masterPartNumber ?? "").trim(),
    masterAssemblyDescription: String(item?.masterAssemblyDescription ?? "").trim(),
    figId,
    figNumber: String(item?.figNumber ?? "").trim(),
    figTitle: String(item?.figTitle ?? "").trim(),
    itemNumber: String(item?.itemNumber ?? "").trim(),
    partNumber: String(item?.partNumber ?? "").trim(),
    partDescription: String(item?.partDescription ?? "").trim(),
  };
};

export async function searchPartsDictionary(
  oem: string,
  keyword: string,
  options?: {
    role?: string;
    vendorId?: number | null;
  }
): Promise<PartsSearchDictionaryResult[]> {
  const normalizedKeyword = String(keyword ?? "").trim();
  if (!normalizedKeyword) return [];

  const params: Record<string, string | number> = { keyword: normalizedKeyword };
  const role = String(options?.role ?? "").trim();
  if (role) params.role = role;

  const vendorIdRaw = Number(options?.vendorId);
  if (Number.isFinite(vendorIdRaw) && vendorIdRaw > 0) {
    params.vendorId = vendorIdRaw;
  }

  const { data } = await api.get<ApiResponse<SearchDicRawItem[]>>(
    `/Parts/searchdic/${encodeURIComponent(String(oem).trim())}`,
    { params }
  );

  if (data?.statusCode === 200 && Array.isArray(data.data)) {
    return data.data.map(normalizeSearchDicItem).filter(Boolean) as PartsSearchDictionaryResult[];
  }

  return [];
}
